import {css} from '@emotion/react'
import styled from '@emotion/styled'

import noImage from '@vio/atlas/images/placeholder-room-image.svg'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'

const hoverOverlayEffect = css`
  ${linariaMq.desktopSm} {
    &:after {
      content: '';
      background-color: rgba(0, 0, 0, 0.2);
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      transition: opacity 0.2s;
      z-index: 1;
      pointer-events: none;
    }

    &:hover::after {
      opacity: 1;
    }
  }
`

/**
 * Mobile: 9:4 aspect ratio for the whole gallery (horizontal layout)
 * - Left side: 2/3 width with one large image
 * - Right side: 1/3 width with two stacked images
 *
 * Desktop: Vertical layout with fixed width
 * - Top: one large image
 * - Bottom: two side-by-side images
 */
export const GalleryWrapper = styled.div`
  position: relative;
  width: 100%;
  border-top-left-radius: ${cssTheme.layout.radius.lg};
  border-top-right-radius: ${cssTheme.layout.radius.lg};
  overflow: hidden;
  flex-shrink: 0;

  /* Mobile: 9:4 aspect ratio for horizontal layout */
  aspect-ratio: 9 / 4;

  ${linariaMq.desktopSm} {
    /* Desktop: Fixed width with calculated height for 3:2 aspect ratio images */
    width: 290px;
    height: 291px;
    cursor: pointer;
    border-bottom-left-radius: ${cssTheme.layout.radius.lg};
    border-bottom-right-radius: ${cssTheme.layout.radius.lg};

    &.--is-dateless {
      aspect-ratio: 3 / 2;
      height: auto;
    }
  }
`

export const GalleryGrid = styled.div`
  display: grid;
  width: 100%;
  height: 100%;
  gap: ${cssTheme.layout.spacing.s100};

  /* Mobile: horizontal 3-image grid layout */
  grid-template-columns: 2fr 1fr;
  grid-template-rows: 1fr 1fr;

  & > :nth-child(1) {
    grid-area: 1 / 1 / 3 / 2;
  }
  & > :nth-child(2) {
    grid-area: 1 / 2 / 2 / 3;
  }
  & > :nth-child(3) {
    grid-area: 2 / 2 / 3 / 3;
  }

  ${linariaMq.desktopSm} {
    /* Desktop: vertical layout with rows proportioned for 3:2 aspect ratio images */
    /* Top row: 2fr, Bottom row: 1fr to maintain aspect ratios */
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 2fr 1fr;

    & > :nth-child(1) {
      grid-area: 1 / 1 / 2 / 3;
    }
    & > :nth-child(2) {
      grid-area: 2 / 1 / 3 / 2;
    }
    & > :nth-child(3) {
      grid-area: 2 / 2 / 3 / 3;
    }
  }
`

export const GridItem = styled.div`
  position: relative;
  overflow: hidden;

  ${hoverOverlayEffect}
`

export const ImageCountBadge = styled.div`
  position: absolute;
  bottom: ${cssTheme.layout.spacing.s300};
  right: ${cssTheme.layout.spacing.s300};
  display: flex;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s200};
  background-color: ${cssTheme.colors.background.neutral.overlay};
  color: ${cssTheme.colors.content.neutral.inverse};
  border-radius: ${cssTheme.layout.radius.md};
  padding: ${cssTheme.layout.spacing.s200};
  ${cssTheme.typography.labelXS}
  z-index: 2;
`

export const SingleImageWrapper = styled.div`
  width: 100%;
  height: 100%;

  ${hoverOverlayEffect}
`

export const NoImagePlaceholder = styled.div<{isBrandVio: boolean}>`
  width: 290px;
  ${({isBrandVio}) => (isBrandVio ? '' : 'filter: grayscale(100%);')}
  height: 290px;
  border-radius: ${cssTheme.layout.radius.lg};
  overflow: hidden;
  object-fit: cover;
  object-position: center;
  flex-shrink: 0;
  background-image: url(${noImage.src});
`
